import React, { useState, useEffect } from 'react';
import { Calendar } from 'lucide-react';

const CustomDateInput = ({ 
   value, 
   onChange, 
   isReadOnly = false,
   className = "",
   showTodayButton = true
}) => {
   const [displayValue, setDisplayValue] = useState('');
   const [showCalendar, setShowCalendar] = useState(false);
   const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
   const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());

   // تحويل التاريخ إلى التنسيق المطلوب (DD/MM/YYYY)
   const formatDate = (dateString) => {
       if (!dateString) return '';
       const date = new Date(dateString);
       if (isNaN(date.getTime())) return '';
       return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1)}/${date.getFullYear()}`;
   };

   // تحويل التاريخ من التنسيق المعروض إلى تنسيق HTML5 (YYYY-MM-DD)
   const parseDate = (displayString) => {
       if (!displayString) return '';
       const [day, month, year] = displayString.split('/');
       if (!day || !month || !year) return '';
       return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
   };

   useEffect(() => {
       setDisplayValue(formatDate(value));
       if (value) {
           const date = new Date(value);
           setSelectedYear(date.getFullYear());
           setSelectedMonth(date.getMonth());
       }
   }, [value]);

   const handleManualChange = (e) => {
    const newValue = e.target.value;
    setDisplayValue(newValue);
    const datePattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

    if (newValue === '') { // إذا تم حذف التاريخ بالكامل
        onChange({ target: { value: '' } });
    } else if (datePattern.test(newValue)) { // إذا كانت القيمة المدخلة تطابق نمط التاريخ
        const parsedDate = parseDate(newValue);
        onChange({ target: { value: parsedDate } });
    }
};


   const handleDoubleClick = (e) => {
       e.preventDefault();
       const input = document.createElement('input');
       input.value = displayValue;
       document.body.appendChild(input);
       input.select();
       document.execCommand('copy');
       document.body.removeChild(input);
   };

   const setToday = () => {
       const today = new Date().toISOString().split('T')[0];
       onChange({ target: { value: today } });
   };

   const months = [
       "يناير", "فبراير", "مارس", "إبريل", "مايو", "يونيو",
       "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"
   ];

   const getDaysInMonth = (year, month) => {
       return new Date(year, month + 1, 0).getDate();
   };

   const renderCalendar = () => {
       const daysInMonth = getDaysInMonth(selectedYear, selectedMonth);
       const firstDay = new Date(selectedYear, selectedMonth, 1).getDay();
       const days = [];
       const dayNames = ["أحد", "إثنين", "ثلاثاء", "أربعاء", "خميس", "جمعة", "سبت"];

       // إضافة أسماء الأيام
       days.push(
           <div key="dayNames" className="grid grid-cols-7 gap-1 mb-2">
               {dayNames.map(day => (
                   <div key={day} className="text-center text-sm font-medium text-gray-600">
                       {day}
                   </div>
               ))}
           </div>
       );

       // إضافة الأيام الفارغة في بداية الشهر
       let currentWeek = [];
       for (let i = 0; i < firstDay; i++) {
           currentWeek.push(<div key={`empty-${i}`} className="h-8"></div>);
       }

       // إضافة أيام الشهر
       for (let day = 1; day <= daysInMonth; day++) {
           const dateString = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
           const isSelected = dateString === value;

           currentWeek.push(
               <button
                   key={day}
                   onClick={() => {
                       onChange({ target: { value: dateString } });
                       setShowCalendar(false);
                   }}
                   className={`h-8 rounded-full flex items-center justify-center
                       ${isSelected 
                           ? 'bg-blue-600 text-white' 
                           : 'hover:bg-blue-100'
                       }`}
               >
                   {day}
               </button>
           );

           if (currentWeek.length === 7) {
               days.push(
                   <div key={`week-${day}`} className="grid grid-cols-7 gap-1">
                       {currentWeek}
                   </div>
               );
               currentWeek = [];
           }
       }

       // إضافة األسبوع الأخير إذا كان غير مكتمل
       if (currentWeek.length > 0) {
           while (currentWeek.length < 7) {
               currentWeek.push(<div key={`empty-end-${currentWeek.length}`} className="h-8"></div>);
           }
           days.push(
               <div key="last-week" className="grid grid-cols-7 gap-1">
                   {currentWeek}
               </div>
           );
       }

       return days;
   };

   useEffect(() => {
       const handleClickOutside = (event) => {
           if (!event.target.closest('.date-input-wrapper')) {
               setShowCalendar(false);
           }
       };
       document.addEventListener('click', handleClickOutside);
       return () => document.removeEventListener('click', handleClickOutside);
   }, []);

   return (
       <div className="flex gap-2 items-center">
           <div className="relative flex-1 date-input-wrapper">
               <div className="flex items-center">
                   <input
                       type="text"
                       value={displayValue}
                       onChange={handleManualChange}
                       onDoubleClick={handleDoubleClick}
                       placeholder="DD/MM/YYYY"
                       className={`${className} w-full`}
                       readOnly={isReadOnly}
                   />
                   <button
                       type="button"
                       onClick={() => setShowCalendar(!showCalendar)}
                       className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                   >
                       <Calendar size={20} />
                   </button>
               </div>

               {showCalendar && !isReadOnly && (
                   <div className="absolute left-0 z-50 mt-1 bg-white border rounded-lg shadow-lg p-4" 
                        style={{ minWidth: '300px' }}>
                       <div className="flex justify-between items-center mb-4">
                           <button
                               onClick={() => setSelectedYear(selectedYear - 1)}
                               className="p-1 hover:bg-gray-100 rounded"
                           >
                               ‹‹
                           </button>
                           <button
                               onClick={() => setSelectedMonth(prev => prev > 0 ? prev - 1 : 11)}
                               className="p-1 hover:bg-gray-100 rounded"
                           >
                               ‹
                           </button>
                           <div className="text-center font-medium">
                               {months[selectedMonth]} ({selectedMonth + 1}) {selectedYear}
                           </div>
                           <button
                               onClick={() => setSelectedMonth(prev => prev < 11 ? prev + 1 : 0)}
                               className="p-1 hover:bg-gray-100 rounded"
                           >
                               ›
                           </button>
                           <button
                               onClick={() => setSelectedYear(selectedYear + 1)}
                               className="p-1 hover:bg-gray-100 rounded"
                           >
                               ››
                           </button>
                       </div>
                       {renderCalendar()}
                   </div>
               )}
           </div>
           {showTodayButton && !isReadOnly && (
               <button
                   type="button"
                   onClick={setToday}
                   className="px-3 py-2.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 text-sm whitespace-nowrap"
               >
                   اليوم
               </button>
           )}
       </div>
   );
};

export default CustomDateInput;